import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Calendar,
  Clock,
  RefreshCw,
  ArrowDown,
  ArrowUp,
  Info,
  Search,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Loader } from '@/components/ui/Loader';
import { Modal } from '@/components/ui/Modal';
import { StatTile } from '@/components/ui/StatTile';
import { useStore } from '@/store/useStore';
import { fetchCallsInRange, fetchExtensionGroups, fetchExtensions } from '@/services/api';
import type { CallRecord, ExtensionGroupInfo } from '@/types';
import {
  classifyCalls,
  extensionCallStats,
  missedCalls,
  type CallsSummary,
  type ClassifiedCall,
  type ExtensionCallStats,
} from '@/lib/reports/callClassification';
import {
  formatDuration,
  getCustomDateRange,
  getThisMonthRange,
  getThisWeekRange,
  getTodayRange,
  getYesterdayRange,
  toHtmlDateInput,
} from '@/utils/helpers';
import toast from 'react-hot-toast';
import { CallDetailPanel } from './CallDetailPanel';
import { MissedCallsList } from './MissedCallsList';
import { StatCallsModal } from './StatCallsModal';
import type { StatCallKind } from './statCalls';
import { showErrorToast } from '@/utils/toast';

type StatsPreset = 'today' | 'yesterday' | 'week' | 'month' | 'custom';
type MetricKey = 'received' | 'made' | 'total' | 'unansweredRings' | 'trulyMissed' | 'duration';
type SortKey = 'extension' | MetricKey;
type SortDirection = 'asc' | 'desc';

/** Missed calls shown on the card before "Show all". */
const MISSED_PREVIEW = 10;

const UNANSWERED_RINGS_TIP =
  "This phone rang but wasn't picked up. The call may have been answered elsewhere.";
const TRULY_MISSED_TIP =
  'Rang here, and the caller never spoke to anyone: the call was missed or went to voicemail.';

const EMPTY_SUMMARY: CallsSummary = {
  received: 0,
  made: 0,
  total: 0,
  answered: 0,
  missed: 0,
  voicemail: 0,
  ivrAbandoned: 0,
  shortSpam: 0,
  internal: 0,
  followedUp: 0,
};

const PRESETS: Array<{ id: StatsPreset; label: string }> = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'week', label: 'This week' },
  { id: 'month', label: 'This month' },
  { id: 'custom', label: 'Custom' },
];

function resolveRange(
  preset: StatsPreset,
  customStart: string,
  customEnd: string
): { startTime: string; endTime: string } {
  switch (preset) {
    case 'yesterday':
      return getYesterdayRange();
    case 'week':
      return getThisWeekRange();
    case 'month':
      return getThisMonthRange();
    case 'custom':
      return getCustomDateRange(customStart, customEnd);
    case 'today':
    default:
      return getTodayRange();
  }
}

function formatDisplayDay(apiDateTime: string): string {
  const datePart = apiDateTime.slice(0, 10).replace(/\//g, '-');
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return apiDateTime.slice(0, 10);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function rangeSummary(startTime: string, endTime: string): string {
  const startDay = startTime.slice(0, 10);
  const endDay = endTime.slice(0, 10);
  if (startDay === endDay) {
    return formatDisplayDay(startTime);
  }
  return `${formatDisplayDay(startTime)} – ${formatDisplayDay(endTime)}`;
}

export function TodayStats() {
  const { setCallStats, extensions, setExtensions, loading, setLoading } = useStore();
  const [preset, setPreset] = useState<StatsPreset>('today');
  const [customStart, setCustomStart] = useState(() => toHtmlDateInput(new Date()));
  const [customEnd, setCustomEnd] = useState(() => toHtmlDateInput(new Date()));
  const [activeRange, setActiveRange] = useState(() => getTodayRange());
  const [rows, setRows] = useState<ExtensionCallStats[]>([]);
  const [extensionGroups, setExtensionGroups] = useState<ExtensionGroupInfo[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState('all');
  const [extensionSearch, setExtensionSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('extension');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [openCalls, setOpenCalls] = useState<StatCallKind | null>(null);
  const [detailCall, setDetailCall] = useState<ClassifiedCall | null>(null);
  /** CDR rows for the active range, and the calls they make up: every number on this card comes from these. */
  const [callRecords, setCallRecords] = useState<CallRecord[] | null>(null);
  const [calls, setCalls] = useState<ClassifiedCall[] | null>(null);
  const [summary, setSummary] = useState<CallsSummary>(EMPTY_SUMMARY);
  const [callRecordsFailed, setCallRecordsFailed] = useState(false);
  const statsRequest = useRef(0);

  const title = useMemo(() => {
    switch (preset) {
      case 'yesterday':
        return "Yesterday's Stats";
      case 'week':
        return "This Week's Stats";
      case 'month':
        return "This Month's Stats";
      case 'custom':
        return 'Custom Range Stats';
      default:
        return "Today's Stats";
    }
  }, [preset]);

  const totalDuration = useMemo(() => rows.reduce((sum, row) => sum + row.duration, 0), [rows]);

  const loadStats = useCallback(
    async (nextPreset: StatsPreset = preset, start = customStart, end = customEnd) => {
      let range: { startTime: string; endTime: string };
      try {
        range = resolveRange(nextPreset, start, end);
      } catch {
        toast.error('Choose a valid start and end date');
        return;
      }

      const request = ++statsRequest.current;
      setLoading('stats', true);
      setActiveRange(range);
      setCallRecords(null);
      setCalls(null);
      setCallRecordsFailed(false);

      try {
        const [exts, groups, records] = await Promise.all([
          fetchExtensions(),
          fetchExtensionGroups(),
          fetchCallsInRange(range.startTime, range.endTime),
        ]);
        if (request !== statsRequest.current) return;
        setExtensions(exts);
        setExtensionGroups(groups);

        const classified = classifyCalls(records, { extensions: exts });
        const stats = extensionCallStats(classified.calls, exts);
        setCallRecords(records);
        setCalls(classified.calls);
        setSummary(classified.summary);
        setRows(stats);
        // Keep store list as combined rows for any other consumers
        setCallStats(
          stats.map((row) => ({
            ext_num: row.ext_num,
            ext_name: row.ext_name,
            total_call_count: row.total,
            no_answer_calls: row.trulyMissed,
            total_talking_time: row.duration,
          }))
        );
      } catch (error: unknown) {
        console.error('Error loading stats:', error);
        if (request !== statsRequest.current) return;
        setCallRecords([]);
        setCalls([]);
        setSummary(EMPTY_SUMMARY);
        setRows([]);
        setCallRecordsFailed(true);
        showErrorToast(error, 'Failed to load statistics', 'today-stats-load-error');
      } finally {
        if (request === statsRequest.current) setLoading('stats', false);
      }
    },
    [customEnd, customStart, preset, setCallStats, setExtensions, setLoading]
  );

  useEffect(() => {
    void loadStats('today');
    // Initial load only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectPreset = (next: StatsPreset) => {
    setPreset(next);
    if (next === 'custom') {
      return;
    }
    void loadStats(next);
  };

  const applyCustomRange = () => {
    setPreset('custom');
    void loadStats('custom', customStart, customEnd);
  };

  const maxDate = toHtmlDateInput(new Date());
  const multiDay = activeRange.startTime.slice(0, 10) !== activeRange.endTime.slice(0, 10);

  // Newest first
  const missedList = useMemo(() => (calls ? missedCalls(calls).reverse() : []), [calls]);
  const missedCaption = [
    `Voicemail ${summary.voicemail}`,
    `IVR hang-ups ${summary.ivrAbandoned}${summary.shortSpam ? ` (+${summary.shortSpam} short/spam)` : ''}`,
  ].join(' · ');

  const visibleRows = useMemo(() => {
    const query = extensionSearch.trim().toLowerCase();
    const selectedGroup = extensionGroups.find((group) => group.id === selectedGroupId);
    const memberNumbers = new Set(selectedGroup?.memberExtensionNumbers || []);

    const filtered = rows.filter((row) => {
      if (
        selectedGroup &&
        selectedGroup.memberSelect !== 'sel_all_ext' &&
        !memberNumbers.has(row.ext_num)
      ) {
        return false;
      }

      if (!query) return true;
      return (
        row.ext_num.toLowerCase().includes(query) ||
        row.ext_name.toLowerCase().includes(query)
      );
    });

    return [...filtered].sort((a, b) => {
      let comparison: number;
      if (sortKey === 'extension') {
        comparison = a.ext_num.localeCompare(b.ext_num, undefined, {
          numeric: true,
          sensitivity: 'base',
        });
      } else {
        comparison = a[sortKey] - b[sortKey];
        if (comparison === 0) {
          comparison = a.ext_num.localeCompare(b.ext_num, undefined, { numeric: true });
        }
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [extensionGroups, extensionSearch, rows, selectedGroupId, sortDirection, sortKey]);

  const sortBy = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((current) => (current === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDirection('desc');
    }
  };

  const sortIndicator = (key: SortKey) => {
    if (sortKey !== key) return null;
    return sortDirection === 'desc' ? (
      <ArrowDown className="w-3.5 h-3.5" />
    ) : (
      <ArrowUp className="w-3.5 h-3.5" />
    );
  };

  return (
    <Card>
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white">{title}</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
              <Calendar size={14} className="shrink-0" />
              {rangeSummary(activeRange.startTime, activeRange.endTime)}
            </p>
          </div>
          <button
            type="button"
            onClick={() => void loadStats()}
            disabled={loading.stats}
            className="inline-flex items-center gap-2 rounded-full border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm font-medium text-gray-800 dark:text-gray-100 hover:border-gray-900 dark:hover:border-gray-300 disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading.stats ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {PRESETS.map((item) => {
            const active = preset === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => selectPreset(item.id)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  active
                    ? 'bg-primary-600 text-white'
                    : 'bg-panel dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {item.label}
              </button>
            );
          })}
        </div>

        {preset === 'custom' && (
          <div className="flex flex-wrap items-end gap-3 p-3 rounded-2xl bg-panel dark:bg-gray-900/60">
            <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
              From
              <input
                type="date"
                value={customStart}
                max={customEnd || maxDate}
                onChange={(e) => setCustomStart(e.target.value)}
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
              To
              <input
                type="date"
                value={customEnd}
                min={customStart}
                max={maxDate}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </label>
            <button
              type="button"
              onClick={applyCustomRange}
              disabled={!customStart || !customEnd || loading.stats}
              className="px-5 py-2 rounded-full bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium disabled:opacity-50"
            >
              Apply
            </button>
          </div>
        )}
      </div>

      {loading.stats ? (
        <Loader text="Loading statistics..." />
      ) : (
        <>
          {/* Overall Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 mb-8">
            <StatTile
              featured
              size="md"
              label="Total calls"
              value={summary.total}
              caption="Received + made"
              onClick={() => setOpenCalls('total')}
              title="Show all calls"
            />
            <StatTile
              size="md"
              surface="inset"
              label="Received"
              value={summary.received}
              caption={`Inbound · ${summary.answered} answered`}
              onClick={() => setOpenCalls('received')}
              title="Show received calls"
            />
            <StatTile
              size="md"
              surface="inset"
              label="Made"
              value={summary.made}
              caption="Outbound calls"
              onClick={() => setOpenCalls('made')}
              title="Show calls made"
            />
            <StatTile
              size="md"
              surface="inset"
              label="Missed"
              value={summary.missed}
              dotClassName="bg-red-500"
              caption={missedCaption}
              onClick={() => setOpenCalls('missed')}
              title="Callers who never spoke to anyone. IVR hang-ups under 5 seconds are not counted anywhere."
            />
            <div className="sm:col-span-2 xl:col-span-1 min-w-0">
              <StatTile
                size="md"
                surface="inset"
                label="Talk time"
                value={<span className="text-2xl">{formatDuration(totalDuration)}</span>}
                icon={Clock}
                caption="Total time on calls"
              />
            </div>
          </div>

          {/* Missed calls: one row per call where the caller never spoke to anyone */}
          {!callRecordsFailed && (
            <div className="mb-8">
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Missed calls
                  <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                    {summary.missed} missed · {summary.voicemail} voicemail
                    {summary.followedUp > 0 && ` · ${summary.followedUp} called back`}
                  </span>
                </h3>
                {missedList.length > MISSED_PREVIEW && (
                  <button
                    type="button"
                    onClick={() => setOpenCalls('missed')}
                    className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
                  >
                    Show all {missedList.length}
                  </button>
                )}
              </div>
              <MissedCallsList
                calls={missedList.slice(0, MISSED_PREVIEW)}
                showDate={multiDay}
                onSelect={setDetailCall}
              />
            </div>
          )}

          {/* Per Extension Stats */}
          {rows.length > 0 ? (
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mr-2">
                  Per extension
                </h3>
                <button
                  type="button"
                  onClick={() => setSelectedGroupId('all')}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    selectedGroupId === 'all'
                      ? 'bg-primary-600 border-primary-600 text-white'
                      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-primary-400'
                  }`}
                >
                  All groups
                </button>
                {extensionGroups.map((group) => {
                  const active = selectedGroupId === group.id;
                  const available = group.membershipAvailable === true;
                  return (
                    <button
                      key={group.id}
                      type="button"
                      disabled={!available}
                      onClick={() => setSelectedGroupId(group.id)}
                      title={
                        available
                          ? `Show ${group.name}`
                          : `${group.name} membership is unavailable from this PBX`
                      }
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        active
                          ? 'bg-primary-600 border-primary-600 text-white'
                          : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-900 dark:hover:border-gray-300'
                      } disabled:opacity-45 disabled:cursor-not-allowed`}
                    >
                      {group.name}
                    </button>
                  );
                })}
              </div>

              <div className="overflow-x-auto rounded-2xl border border-gray-200 dark:border-gray-700">
                <table className="w-full text-gray-900 dark:text-gray-100">
                  <thead className="bg-panel dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                    <tr>
                      <th
                        className="min-w-[360px] px-4 py-2.5 text-left text-sm font-semibold text-gray-700 dark:text-gray-200"
                        aria-sort={
                          sortKey === 'extension'
                            ? sortDirection === 'desc'
                              ? 'descending'
                              : 'ascending'
                            : 'none'
                        }
                      >
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => sortBy('extension')}
                            className="inline-flex shrink-0 items-center gap-1.5 text-gray-700 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400"
                          >
                            Extension
                            {sortIndicator('extension')}
                          </button>
                          <div className="relative min-w-[170px] max-w-[240px] flex-1">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                            <input
                              type="search"
                              value={extensionSearch}
                              onChange={(event) => setExtensionSearch(event.target.value)}
                              placeholder="Search extension..."
                              aria-label="Search extensions"
                              className="w-full rounded-md border border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-800 py-1.5 pl-8 pr-2 text-xs font-normal text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                          </div>
                        </div>
                      </th>
                      <th
                        className="px-4 py-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-300"
                        aria-sort={
                          sortKey === 'received'
                            ? sortDirection === 'desc'
                              ? 'descending'
                              : 'ascending'
                            : 'none'
                        }
                      >
                        <button
                          type="button"
                          onClick={() => sortBy('received')}
                          className="inline-flex items-center justify-center gap-1.5 hover:text-primary-600 dark:hover:text-primary-400"
                        >
                          Received {sortIndicator('received')}
                        </button>
                      </th>
                      <th
                        className="px-4 py-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-300"
                        aria-sort={
                          sortKey === 'made'
                            ? sortDirection === 'desc'
                              ? 'descending'
                              : 'ascending'
                            : 'none'
                        }
                      >
                        <button
                          type="button"
                          onClick={() => sortBy('made')}
                          className="inline-flex items-center justify-center gap-1.5 hover:text-primary-600 dark:hover:text-primary-400"
                        >
                          Made {sortIndicator('made')}
                        </button>
                      </th>
                      <th
                        className="px-4 py-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-300"
                        aria-sort={
                          sortKey === 'total'
                            ? sortDirection === 'desc'
                              ? 'descending'
                              : 'ascending'
                            : 'none'
                        }
                      >
                        <button
                          type="button"
                          onClick={() => sortBy('total')}
                          className="inline-flex items-center justify-center gap-1.5 hover:text-primary-600 dark:hover:text-primary-400"
                        >
                          Total {sortIndicator('total')}
                        </button>
                      </th>
                      <th
                        className="px-4 py-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-300"
                        title={UNANSWERED_RINGS_TIP}
                        aria-sort={
                          sortKey === 'unansweredRings'
                            ? sortDirection === 'desc'
                              ? 'descending'
                              : 'ascending'
                            : 'none'
                        }
                      >
                        <button
                          type="button"
                          onClick={() => sortBy('unansweredRings')}
                          className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap hover:text-primary-600 dark:hover:text-primary-400"
                        >
                          Unanswered rings
                          <Info className="w-3.5 h-3.5 text-gray-400" aria-hidden="true" />
                          {sortIndicator('unansweredRings')}
                        </button>
                      </th>
                      <th
                        className="px-4 py-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-300"
                        title={TRULY_MISSED_TIP}
                        aria-sort={
                          sortKey === 'trulyMissed'
                            ? sortDirection === 'desc'
                              ? 'descending'
                              : 'ascending'
                            : 'none'
                        }
                      >
                        <button
                          type="button"
                          onClick={() => sortBy('trulyMissed')}
                          className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap hover:text-primary-600 dark:hover:text-primary-400"
                        >
                          Truly missed
                          <Info className="w-3.5 h-3.5 text-gray-400" aria-hidden="true" />
                          {sortIndicator('trulyMissed')}
                        </button>
                      </th>
                      <th
                        className="px-4 py-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-300"
                        aria-sort={
                          sortKey === 'duration'
                            ? sortDirection === 'desc'
                              ? 'descending'
                              : 'ascending'
                            : 'none'
                        }
                      >
                        <button
                          type="button"
                          onClick={() => sortBy('duration')}
                          className="inline-flex items-center justify-center gap-1.5 hover:text-primary-600 dark:hover:text-primary-400"
                        >
                          Duration {sortIndicator('duration')}
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800/70 text-gray-900 dark:text-gray-100 divide-y divide-gray-200 dark:divide-gray-700">
                    {visibleRows.length > 0 ? (
                      visibleRows.map((row, index) => (
                        <motion.tr
                          key={row.ext_num}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: Math.min(index, 12) * 0.03 }}
                          className="text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/60"
                        >
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                            {row.ext_name} ({row.ext_num})
                          </td>
                          <td className="px-4 py-3 text-center text-sm tabular-nums">
                            {row.received}
                          </td>
                          <td className="px-4 py-3 text-center text-sm tabular-nums">
                            {row.made}
                          </td>
                          <td className="px-4 py-3 text-center text-sm font-semibold tabular-nums text-gray-900 dark:text-white">
                            {row.total}
                          </td>
                          <td
                            className="px-4 py-3 text-center text-sm tabular-nums text-gray-600 dark:text-gray-300"
                            title={UNANSWERED_RINGS_TIP}
                          >
                            {row.unansweredRings}
                          </td>
                          <td
                            className={`px-4 py-3 text-center text-sm tabular-nums ${
                              row.trulyMissed > 0 ? 'font-semibold text-red-600 dark:text-red-400' : ''
                            }`}
                          >
                            {row.trulyMissed}
                          </td>
                          <td className="px-4 py-3 text-center text-sm tabular-nums text-gray-900 dark:text-white">
                            {formatDuration(row.duration)}
                          </td>
                        </motion.tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                        >
                          No extensions match the selected group and search.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="text-center text-gray-500 dark:text-gray-400 py-8">
              No call statistics available for{' '}
              {rangeSummary(activeRange.startTime, activeRange.endTime)}
            </p>
          )}
        </>
      )}

      <StatCallsModal
        kind={openCalls}
        calls={calls}
        records={callRecords}
        failed={callRecordsFailed}
        rangeLabel={rangeSummary(activeRange.startTime, activeRange.endTime)}
        multiDay={multiDay}
        extensions={extensions}
        onClose={() => setOpenCalls(null)}
      />

      <Modal
        isOpen={detailCall !== null}
        onClose={() => setDetailCall(null)}
        title={`Missed call · ${rangeSummary(activeRange.startTime, activeRange.endTime)}`}
        size="xl"
      >
        {detailCall && callRecords && (
          <CallDetailPanel
            record={detailCall.record}
            records={callRecords}
            extensions={extensions}
            backLabel="Close"
            onBack={() => setDetailCall(null)}
          />
        )}
      </Modal>
    </Card>
  );
}
